// Shared contact-form validation. Used by the client form (inline field
// errors) and re-validated on the server inside submitLead.
import { z } from "zod";

export const LeadFieldsSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Please enter your name.")
    .max(100, "Name must be under 100 characters.")
    .regex(/^[\p{L}\p{M}\p{Pd}\p{Zs}'’.]+$/u, "Name contains invalid characters."),
  email: z
    .string()
    .trim()
    .min(1, "Email is required.")
    .email("Enter a valid email address.")
    .max(255, "Email must be under 255 characters."),
  phone: z
    .string()
    .trim()
    .max(40, "Phone must be under 40 characters.")
    .regex(/^[+\d\s().-]*$/, "Enter a valid phone number.")
    .optional()
    .or(z.literal("")),
  company: z
    .string()
    .trim()
    .max(150, "Company must be under 150 characters.")
    .optional()
    .or(z.literal("")),
  message: z
    .string()
    .trim()
    .min(20, "Please add a little more detail (at least 20 characters).")
    .max(1000, "Message must be under 1000 characters."),
});

export type LeadFields = z.infer<typeof LeadFieldsSchema>;

/** Content heuristics shared by client and server. Returns an error or null. */
export function detectSpam(fields: {
  name: string;
  message: string;
  company?: string;
}): string | null {
  const blob = `${fields.name} ${fields.company ?? ""} ${fields.message}`;
  const links = blob.match(/https?:\/\/|www\.|\[url|<a\s/gi) ?? [];
  if (links.length > 2) return "Too many links in the message.";
  if (/\b(viagra|casino|crypto\s*airdrop|seo\s*services|backlinks|loan\s*offer)\b/i.test(blob))
    return "This message was flagged as spam.";
  // Repeated identical characters / gibberish blocks.
  if (/(.)\1{15,}/.test(fields.message)) return "This message was flagged as spam.";
  return null;
}

/** Map a ZodError to a { field: message } record. */
export function fieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] ?? "form");
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}
